import type { ContactProfileResponse } from "@/types/contactProfile.ts";

/**
 * Display helpers for a contact profile, shared by web's contact sidebar and
 * native's contact detail screen. The stored profile is machine-shaped — lower-
 * cased enums, raw URLs — and every surface that shows one has to make the same
 * decisions about it, so they're made once here.
 */

/** Capitalize the first letter for display (profile enum values are lowercased). */
export const capitalize = (value: string): string => value.charAt(0).toUpperCase() + value.slice(1);

/** "VIP" reads wrong title-cased, and it's the one tier that's an initialism. */
export const importanceLabel = (tier: string): string =>
  tier === "vip" ? "VIP" : capitalize(tier);

export const channelLabel: Record<
  NonNullable<ContactProfileResponse["preferredChannel"]>,
  string
> = {
  email: "Email",
  text: "Text",
  slack: "Slack",
};

/** A profile URL's bare hostname (sans `www.`) for a compact link label; the raw URL on a miss. */
export const urlLabel = (url: string): string => {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
};

/**
 * Only http(s) URLs are safe to open — `profileUrls` is free-form research
 * output, so a stored `javascript:`/`data:` value must never become a tappable
 * link.
 */
export const isHttpUrl = (url: string): boolean => {
  try {
    const { protocol } = new URL(url);
    return protocol === "http:" || protocol === "https:";
  } catch {
    return false;
  }
};

/**
 * The relationship chips a profile earns — importance, temperature, and the kind
 * of relationship it is. Empty when the profile is absent or says none of them,
 * which is what lets a caller drop the whole section.
 */
export const relationshipTags = (
  profile: ContactProfileResponse | null | undefined,
): { label: string; value: string }[] =>
  [
    profile?.importanceTier && {
      label: "Importance",
      value: importanceLabel(profile.importanceTier),
    },
    profile?.temperature && { label: "Temperature", value: capitalize(profile.temperature) },
    (profile?.relationshipTypeExact || profile?.relationshipType) && {
      label: "Type",
      value: profile.relationshipTypeExact ?? capitalize(profile.relationshipType ?? ""),
    },
  ].filter((tag): tag is { label: string; value: string } => Boolean(tag));

/**
 * The short prose a surface shows about a contact without opening their bio.
 *
 * The research skill omits any section it has no grounded content for, so plenty
 * of profiles carry a `writeup` and no `summary` — reading only `summary` leaves
 * those contacts looking blank next to the ones that happen to have both. Falls
 * back to the write-up's opening paragraph, which is what a summary would have
 * been drawn from anyway, skipping headings so the blurb is prose rather than a
 * section title.
 */
export const profileBlurb = (
  profile: ContactProfileResponse | null | undefined,
): string | undefined => {
  const summary = profile?.summary?.trim();
  if (summary) return summary;
  const paragraph = (profile?.writeup ?? "")
    .split(/\n\s*\n/)
    .map((block) => block.trim())
    .find((block) => block.length > 0 && !block.startsWith("#"));
  return paragraph || undefined;
};

/** How often a surface re-checks for the bio while a build it started is running. */
export const PROFILE_BUILD_POLL_MS = 10_000;

/**
 * How long a surface waits on a build it started before offering the button again.
 * A build is a minutes-long research run. One the job skipped or that failed never
 * produces a bio, and nothing else reports that, so this is when the surface stops
 * claiming a build is underway. It keeps polling afterwards, because a slow build
 * can still land.
 */
export const PROFILE_BUILD_WAIT_MS = 10 * 60 * 1000;

/**
 * Where a Bio build stands for a contact with no profile, as known to the surface
 * that started it. The server keeps no build state, so a reload forgets it, and a
 * second click is harmless because the job collapses it onto the build in progress.
 *
 * - `idle`: no build started from here. Offer the button.
 * - `building`: a build started from here, still inside the wait.
 * - `slow`: the wait ran out without a bio. Offer the button again, and keep polling.
 */
export type BioBuildPhase = "idle" | "building" | "slow";

/** The words every surface uses for a Bio build, so web and native can't drift. */
export const BIO_BUILD_COPY = {
  idle: "No bio yet for this contact.",
  building: "Building bio… This takes a few minutes.",
  slow: "This is taking longer than expected. You can try again, or check back later.",
  build: "Build bio",
  retry: "Try again",
  startFailed: "Couldn't start the build. Please try again.",
} as const;
